import { datadogRum } from '@datadog/browser-rum';
import { datadogLogs } from '@datadog/browser-logs';
import StructuredData from 'components/StructuredData';
import {
  structuredOrgData,
  structuredFaq,
} from 'components/StructuredData/structuredDataHelpers';
import React from 'react';
import { Helmet } from 'react-helmet';

import { FaqSchema } from 'utils/types';
import { isFullUrl } from 'utils/helpers';
import { NO_INDEX_SLUGS } from 'utils/constants/products';
import { JSONLDSchema } from 'components/StructuredData/types';

const config = require('../../utils/constants/siteConfig');

/** Datadog initialization */
if (process.env.NODE_ENV !== 'test') {
  const ddConfig = {
    clientToken: process.env.DATADOG_CLIENT_TOKEN || '',
    site: 'datadoghq.com',
    service: 'www',
    env: process.env.REACT_APP_ENV,
    sessionSampleRate: 100,
    silentMultipleInit: true,
  };

  datadogRum.init({
    ...ddConfig,
    applicationId: process.env.DATADOG_APPLICATION_ID || '',
    sessionReplaySampleRate: 0,
    trackUserInteractions: true,
  });

  datadogLogs.init({
    ...ddConfig,
    forwardErrorsToLogs: true,
    telemetrySampleRate: 0,
  });
}

export interface SeoProps {
  title?: string;
  description?: string;
  keywords?: string;
  url?: string;
  faqSchema?: FaqSchema;
  slug?: string;
  canonicalUrl?: string;
  noIndex?: boolean;
  structuredDataOverride?: Partial<JSONLDSchema> | Partial<JSONLDSchema>[];
}

export const SEO = ({
  title,
  description,
  keywords,
  url,
  faqSchema,
  slug,
  canonicalUrl,
  noIndex,
  structuredDataOverride,
}: SeoProps): JSX.Element => {
  const isNoIndex = noIndex || NO_INDEX_SLUGS.includes(slug ?? '');
  return (
    <>
      <Helmet htmlAttributes={{ lang: 'en' }}>
        {/* NOTE: OSANO SCRIPT MUST BE THE FIRST LOADED SCRIPT IN ORDER TO PROPERLY BLOCK LOADING OF COOKIES */}
        <script>
          {`
            ;(function(w,o,d){w[o]=w[o]||function(){w[o][d].push(arguments)};w[o][d]=w[o][d]||[]})(window,'Osano','data');
            window.Osano('onInitialized', consent => hideRejectAllButton());
            function hideRejectAllButton() {
              const noRejectAllStates = ['us-ca', 'us-co', 'us-ct', 'us-ut', 'us-va', 'us-wa', 'us-nv', 'us-tx', 'us-or', 'us-mt'];
              if (!noRejectAllStates.includes(window.Osano.cm.jurisdiction)) {
                document.querySelector('.osano-cm-deny').style.display = 'none !important'
              }
            }
          `}
        </script>
        <script type="text/javascript">
          {`
            const embedded = window.location.href.includes('embedded=true');
            // if not embedded, add osano
            if (!embedded) {
              //add osano styles
              const osanoStyle = document.createElement('link');
              osanoStyle.setAttribute('rel', 'stylesheet');
              osanoStyle.setAttribute('type', 'text/css');
              osanoStyle.setAttribute(
                'href',
                'https://cdn.everlywell.com/osano/bannerv2.css',
              );
              document.head.appendChild(osanoStyle);

              // add osano script tag
              const osanoScript = document.createElement('script');
              osanoScript.setAttribute('type', 'text/javascript');
              osanoScript.setAttribute('data-cfasync', 'false');
              osanoScript.setAttribute(
                'src',
                'https://cmp.osano.com/16A6XpTloLmXW5QGm/f6841b3f-6b09-4eb8-8941-4dbc7324ab27/osano.js?language=en&variant=three',
              );
              osanoScript.setAttribute('id', 'osanoScript');
              document.head.appendChild(osanoScript);
            }
            `}
        </script>
        <style type="text/css">{`
          .osano-cm-info-dialog-header {
            top: 0 !important;
          }
        `}</style>
        {/* Level Access Script */}
        <script
          type="text/javascript"
          src="https://cdn.levelaccess.net/accessjs/YW1wMTMwOTk/access.js"
          defer
        ></script>
        {/* Start VWO Async SmartCode */}
        <script data-cfasync="false" type="text/javascript" id="vwoCode">
          {`
        window._vwo_code || (function() {
        var account_id=533964,
        version=2.1,
        settings_tolerance=5000,
        hide_element='body',
        hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
        /* DO NOT EDIT BELOW THIS LINE */
        f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var i=this.getSettings(),n=d.createElement('script'),r=this;t=t||{};if(i){n.textContent=i;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200){_vwo_code.addScript({text:o.responseText})}else{_vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}_vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var i=this.hide_element(),n=i?i+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=n;else t.appendChild(d.createTextNode(n));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var n=d.createElement('div');n.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;';n.setAttribute('id','_vis_opt_path_hides');n.classList.add('_vis_hide_layer');t.parentNode.insertBefore(n,t.nextSibling)}var o='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:o})}else{this.load(o+'&x=true')}}};w._vwo_code=code;code.init();})();
      `}
        </script>

        {/* This is the Mixpanel integration with VWO.
      It needs to be included on the page if the Experiment is running
      on a particular page */}
        <script type="text/javascript">
          {`
        var dataSendingTimer,
        tryPushingDataTimer,
        _vis_data = {};

        window._CURRENT_CAMPAIGNS_EW_VWO = {};
        window.VWO = window.VWO || [];
        window.VWO.push(['onVariationApplied', function (data) {
          if(!data) {
              return;
          }
          var expId = data[1],
              variationId = data[2];
          if (typeof(_vwo_exp[expId].comb_n[variationId]) != 'undefined' && ['VISUAL_AB', 'VISUAL', 'SPLIT_URL', 'SURVEY'].indexOf(_vwo_exp[expId].type) > -1) {
              var test = "VWO-Test-ID-" + expId;
              var variant = _vwo_exp[expId].comb_n[variationId];

              _vis_data[test] = variant;
              window._CURRENT_CAMPAIGNS_EW_VWO = _vis_data;
              clearTimeout(dataSendingTimer);
              (function tryPushingData() {
                  clearTimeout(tryPushingDataTimer);
                  if (!window.mixpanel || !window.mixpanel.track) {
                      tryPushingDataTimer = setTimeout(function () {
                          tryPushingData();
                      }, 5);
                      return;
                  }
                  dataSendingTimer = setTimeout(function () {
                      window.mixpanel.track("VWO", _vis_data);
                      _vis_data = {};
                  }, 100);
              })();
            }
        }]);
      `}
        </script>
        {/* General tags */}
        <title>{title || config.title}</title>
        <meta
          name="description"
          content={description || config.siteDescription}
        />
        <meta name="keywords" content={keywords || config.keywords} />
        {/* Canonical URL is a Contentful field available in the Pages model.
        If the field is blank, Gastby Helmet plugin will take over and generate the
        Canonical URL based on the page slug.
        */}
        {canonicalUrl && !isFullUrl(canonicalUrl) ? (
          <link
            rel="canonical"
            key={`${config.siteUrl}/${canonicalUrl}/`}
            href={`${config.siteUrl}/${canonicalUrl}/`}
          />
        ) : canonicalUrl ? (
          <link
            rel="canonical"
            key={`${canonicalUrl}/`}
            href={`${canonicalUrl}/`}
          />
        ) : (
          []
        )}
        {/* OpenGraph tags */}
        <meta property="og:title" content={title || config.title} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={url || config.siteUrl} />
        <meta
          property="og:description"
          content={description || config.siteDescription}
        />
        {/* Twitter Card tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:creator"
          content={config.userTwitter ? config.userTwitter : ''}
        />
        <meta name="twitter:title" content={title || config.siteTitle} />
        <meta
          name="twitter:description"
          content={description || config.siteDescription}
        />
        {/* Disable iPhone auto zoom on select drop down menus */}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1"
        ></meta>
        {isNoIndex ? (
          <meta name="robots" content="noindex, nofollow, noimageindex" />
        ) : null}
      </Helmet>
      {/* Schema.org tags */}
      {/* Organization Schema Attributes from config */}
      <StructuredData data={structuredDataOverride || structuredOrgData} />
      {/* FAQ Schema Attributes from Contentful */}
      {faqSchema?.length ? (
        <StructuredData data={structuredFaq(faqSchema)} />
      ) : null}
    </>
  );
};
